import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

export default function AddonInstall({ asset, onImported, onCancel }) {
  const [dependencies, setDependencies] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [retry, setRetry] = useState(0);
  const lock = useRef(false);
  const active = useRef(false);

  useEffect(() => {
    let current = true;
    active.current = true;
    setDependencies(null);
    setError(null);
    axios.get(`/api/workshop/item/${encodeURIComponent(asset.workshopId)}/dependencies`)
      .then(({ data }) => {
        if (!Array.isArray(data) || !data.some(mod => mod?.workshopId?.toUpperCase() === asset.workshopId.toUpperCase())) {
          throw new Error("Incomplete dependencies");
        }
        if (current) setDependencies(data.filter(mod => mod.workshopId.toUpperCase() !== asset.workshopId.toUpperCase()));
      }).catch(() => { if (current) setError("Addon dependencies could not be loaded. Please try again."); });
    return () => { current = false; active.current = false; };
  }, [asset.workshopId, retry]);

  const install = async () => {
    if (lock.current || dependencies === null) return;
    lock.current = true;
    setImporting(true);
    setError(null);
    try {
      const { data } = await axios.post(`/api/workshop/item/${encodeURIComponent(asset.workshopId)}/import-mods`);
      if (!Array.isArray(data.config?.game?.mods) || !Number.isInteger(data.importedCount) || data.importedCount < 0) throw new Error("Invalid import response");
      // Propagate completed writes even if the dialog was closed during the request.
      onImported(data.config, data.importedCount);
    } catch {
      if (active.current) setError("Addon could not be installed. Please try again.");
    } finally {
      lock.current = false;
      if (active.current) setImporting(false);
    }
  };

  return <>
    <h3>{asset.title}</h3>
    {dependencies === null && !error && <p role="status">Loading dependencies…</p>}
    {error && <p role="alert" className="workshop-error">{error}</p>}
    {dependencies === null && error && <button type="button" onClick={() => setRetry(value => value + 1)}>Retry</button>}
    {dependencies !== null && (dependencies.length ? <>
      <p>This addon requires:</p>
      <ul>{dependencies.map(mod => <li key={mod.workshopId}>{mod.name || mod.workshopId}</li>)}</ul>
      <p>Install all required mods?</p>
    </> : <p>Dependencies: None</p>)}
    <div className="workshop-card-actions">
      <button type="button" className="button-secondary" disabled={importing} onClick={onCancel}>Cancel</button>
      <button type="button" disabled={dependencies === null || importing} onClick={install}>Add To Server</button>
    </div>
    {importing && <p role="status">Adding mods…</p>}
  </>;
}
